import React, { useCallback } from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import HTTP from 'services/http';
import { Grid, Button } from '@mui/material';
import { ShippingClient } from 'clients/shipping-client';
import { buildQaId } from 'utils/build-qa-id';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import axios, { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { editTask } from 'actions/action-tasks';
import { FillDeliveryConfirmationStatus } from 'interfaces/enums/TaskStatuses/FillDeliveryConfirmationStatus';
import { IPrintLabelButton } from './types';
import { ShippingStyles } from './shipping.styles';
import { FDC, EDIT_FILL_DELIVERY_CONFIRMATION } from '../../constants/index';
import { DeliveryMethodValue } from '../../constants/enums';

const qaIdBuilder = buildQaId('shipping');

interface IShippingProps extends IPrintLabelButton, WithStyles<typeof ShippingStyles> {}

const VoidLabelButton = (props: IShippingProps): JSX.Element | null => {
  const {
    classes,
    orderId,
    disabled,
    currentFdcInformation,
    currentDeliveryInformation,
    setFdcOrderDetails = () => {},
    updateShippingPage = () => {},
    pharmacyInfo,
  } = props;
  const dispatch = useDispatch();
  const deliveryMethod = currentDeliveryInformation?.deliveryMethod;
  const [updating, setUpdating] = React.useState<boolean>(false);

  const getVoidLabel = useCallback(
    async (orderIdValue: string) => {
      setUpdating(true);
      try {
        const void_label_response = await ShippingClient.voidLabel(orderIdValue);
        console.log('void_label_response: ', void_label_response);
        let new_status_id = currentFdcInformation?.statusId;
        switch (pharmacyInfo?.workflow_type) {
          case 0: // Simple
          case 1: // Full
          case 2: // Partial
            if (
              [DeliveryMethodValue.Ups, DeliveryMethodValue.Courier].includes(
                deliveryMethod as number,
              ) &&
              currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Shipped
            ) {
              new_status_id = FillDeliveryConfirmationStatus.Packed;
            }
            if (
              [DeliveryMethodValue.InternalDelivery].includes(deliveryMethod as number) &&
              (currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Shipped ||
                currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp)
            ) {
              new_status_id = FillDeliveryConfirmationStatus.Packed;
            }
            break;
          default:
        }
        const updatedTask = {
          id: currentFdcInformation?.id,
          taskType: FDC,
          therapy_id: currentFdcInformation?.therapyId,
          status_id: new_status_id,
        };
        const patchUrl = `/tasks/fdc/${updatedTask.id}`;
        const response = await HTTP.patch(patchUrl, updatedTask, {});
        const reduxPayload = {
          type: EDIT_FILL_DELIVERY_CONFIRMATION,
          payload: response,
        };
        dispatch(reduxPayload);
        dispatch?.(await editTask(updatedTask, FDC));
        dispatch(notifySuccess('The order label has been voided.'));
      } catch (error) {
        console.log('getVoidLabel error: ', error);
        // Provide the user with a descriptive message, including the AxiosError message if present
        let responseErrorMessage;
        if (axios.isAxiosError(error)) {
          const responseError = error as AxiosError<{ message?: string }>;
          responseErrorMessage =
            responseError.response?.data?.message ?? responseError.response?.data;
        } else {
          responseErrorMessage = null;
        }
        const generateVoidLabelMessage = 'Unable to void/cancel shipping label.';
        const notifyMessage =
          responseErrorMessage == null
            ? generateVoidLabelMessage
            : `${generateVoidLabelMessage} ${responseErrorMessage}`;
        dispatch(notifyError(notifyMessage));
      } finally {
        setUpdating(false);
        updateShippingPage();
      }
    },
    [currentFdcInformation?.id, currentFdcInformation?.therapyId, dispatch, setFdcOrderDetails],
  );

  return (
    <Grid item className={classes.voidLabelButton}>
      <Button
        variant="contained"
        color="primary"
        onClick={() => getVoidLabel(orderId)}
        data-qa-id={qaIdBuilder('button.void-label')}
        disabled={disabled || updating}
      >
        {updating ? '....Voiding Label' : 'Void Label'}
      </Button>
    </Grid>
  );
};

export default withStyles(ShippingStyles)(VoidLabelButton);
